import React, { useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { Camera, RotateCcw } from "lucide-react";

export function PhotoCapture({ photo, onCapture, label = "Client photo (selfie)", required = true, testid = "photo" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [err, setErr] = useState("");

  const start = async () => {
    setErr("");
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = s;
      setStreaming(true);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); } }, 50);
    } catch { setErr("Camera unavailable or permission denied."); }
  };
  const stop = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); setStreaming(false); };
  const capture = () => {
    const v = videoRef.current;
    const c = document.createElement("canvas");
    c.width = v.videoWidth || 640; c.height = v.videoHeight || 480;
    c.getContext("2d").drawImage(v, 0, 0);
    onCapture(c.toDataURL("image/jpeg", 0.85));
    stop();
  };
  useEffect(() => () => stop(), []);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">{label}{required && <span className="text-destructive"> *</span>}</span>
        {photo && (
          <Button type="button" variant="ghost" size="sm" className="h-8 text-muted-foreground" onClick={() => { onCapture(null); start(); }} data-testid={`${testid}-retake`}>
            <RotateCcw className="mr-1 h-4 w-4" /> Retake
          </Button>
        )}
      </div>
      {photo ? (
        <img src={photo} alt="Captured" className="aspect-[4/3] w-full rounded-2xl border border-border object-cover" data-testid={`${testid}-preview`} />
      ) : streaming ? (
        <div className="space-y-2">
          <video ref={videoRef} playsInline muted className="aspect-[4/3] w-full rounded-2xl border border-border bg-black object-cover" />
          <div className="flex gap-2">
            <Button type="button" className="h-12 flex-1 rounded-full" onClick={capture} data-testid={`${testid}-capture`}><Camera className="mr-2 h-4 w-4" /> Take photo</Button>
            <Button type="button" variant="ghost" className="h-12" onClick={stop}>Cancel</Button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={start} data-testid={`${testid}-open`}
          className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border bg-muted/40 text-sm font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground">
          <Camera className="h-7 w-7" /> Open camera
        </button>
      )}
      {err && <p className="text-xs text-destructive" data-testid={`${testid}-error`}>{err}</p>}
    </div>
  );
}
